import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Quotation, EmissionLog } from 'data-layer-cr/dist/models';
import { makeEmptyQuotation } from 'data-layer-cr/dist/models/Quotation';

/**
 * If the quotation value is equal to this, we can assume that the quotation is
 * "empty". The fastest way is to check if the id is -1. We use this definition to
 * avoid handling an optional value in the slice (Quotation | undefined)
 */
export const emptyQuotation: Quotation = {
  ...makeEmptyQuotation(),
  id: -1,
};

export type QuotationState = {
  quotation: Quotation;
  emissionLog: EmissionLog | null;
  canGoBackToUnitSelection: boolean;
  localSelectedPackageOption: number;
  hasLicense: boolean;
  hasSerialMotor: boolean;
};

export const initialState: QuotationState = {
  quotation: emptyQuotation,
  emissionLog: null,
  canGoBackToUnitSelection: false,
  localSelectedPackageOption: -1,
  hasLicense: false,
  hasSerialMotor: false,
};

const quotationSlice = createSlice({
  name: 'quotation',
  initialState,
  reducers: {
    overwriteQuotation: (
      _,
      action: PayloadAction<QuotationState>,
    ): QuotationState => {
      return action.payload;
    },
    requestQuotationSuccess: (
      state,
      action: PayloadAction<Quotation>,
    ): void => {
      state.canGoBackToUnitSelection = true;
      state.quotation = action.payload;
    },
    setEmissionLog: (
      state,
      action: PayloadAction<EmissionLog | null>,
    ): void => {
      state.emissionLog = action.payload;
    },
    setSelectedPackageOption: (state, action: PayloadAction<number>): void => {
      state.quotation = {
        ...state.quotation,
        packageOption: action.payload,
      };
      state.localSelectedPackageOption = action.payload;
    },
    setLocalSelectedPackageOption: (
      state,
      action: PayloadAction<number>,
    ): void => {
      state.localSelectedPackageOption = action.payload;
    },
    setAutoPxFlag: (state, action: PayloadAction<boolean>): void => {
      state.quotation = {
        ...state.quotation,
        isAutoPx: action.payload,
      };
    },
    setHasLicense: (state, action: PayloadAction<boolean>): void => {
      state.hasLicense = action.payload;
    },
    setHasSerialMotor: (state, action: PayloadAction<boolean>): void => {
      state.hasSerialMotor = action.payload;
    },
    updateQuotationStatus: (state, action: PayloadAction<number>): void => {
      state.quotation = {
        ...state.quotation,
        status: {
          ...state.quotation.status,
          id: action.payload,
        },
      };
    },
  },
});

export const {
  overwriteQuotation,
  requestQuotationSuccess,
  setSelectedPackageOption,
  setEmissionLog,
  setAutoPxFlag,
  setHasLicense,
  updateQuotationStatus,
  setLocalSelectedPackageOption,
  setHasSerialMotor,
} = quotationSlice.actions;

export default quotationSlice.reducer;
